import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { IAddress } from "../../../models/address";
import { IReduxState } from "../reducers.interfaces";
import { getBillingAddress } from "../utils";

/**
 * Arguments for saveBillingAddress thunk.
 */
interface SaveBillingAddressArgs {
    bypassUSPSValidation?: boolean;
}

/**
 * Save the billing address for checkout.
 *
 * This thunk saves the billing address to the checkout session for
 * payment processing. Supports bypassing USPS address validation for
 * cases where the user confirms their non-standard address.
 *
 * @param {boolean} [args.bypassUSPSValidation=false] - Skip USPS validation for non-standard addresses
 * @returns {Promise<IAddress>} Promise resolving to the saved address (potentially normalized by USPS)
 *
 * @example
 * // Save billing address with validation
 * dispatch(saveBillingAddress({}));
 *
 * @example
 * // Save non-standard address bypassing validation
 * dispatch(saveBillingAddress({ bypassUSPSValidation: true }));
 *
 * @throws {Error} If address validation fails and bypass not set
 *
 * @sideEffects
 * - Reads billing address from `state.form.billing_*` fields
 * - Makes API call to save address via checkoutAPI.saveBillingAddress
 * - May normalize address fields via USPS validation
 */
export const saveBillingAddress = createAsyncThunk(
    "checkout/saveBillingAddress",
    async (args: SaveBillingAddressArgs, { getState }): Promise<IAddress> => {
        const state = getState() as { checkout: IReduxState };
        const billingAddr = getBillingAddress(state.checkout);
        return checkoutAPI.saveBillingAddress(
            billingAddr,
            args.bypassUSPSValidation,
        );
    },
);
